/**
 * Masked pose layers (docs/SKEL-STORY.md Q6): a `hold` arm pose over any base clip.
 *
 * The base clip is sampled as usual; the bones of a mask (the arm chain on the holding side) then
 * take their LOCAL values from the layer clip, blended by `w`. Everything else — including the
 * parents of the masked chain — stays the base pose, so the arm rides the walking torso.
 */
import { clamp, lerp, lerp2, shortestDelta } from './math'
import type { Pose } from './animate'
import type { SkfRoot } from './types'

/** Indices of the named bones (exact name, then case-insensitive); unknown names are skipped. */
export function maskBoneIds(root: SkfRoot, names: readonly string[]): number[] {
  const out = new Set<number>()
  for (const name of names) {
    let i = root.bones.findIndex((b) => b.name === name)
    if (i < 0) {
      const want = name.toLowerCase()
      i = root.bones.findIndex((b) => b.name.toLowerCase() === want)
    }
    if (i >= 0) out.add(i)
  }
  return [...out].sort((a, b) => a - b)
}

/**
 * `base` with the bones in `ids` (and their visuals) moved toward `layer` by `w` ∈ [0, 1]:
 * positions/scales/tints lerp, rotations take the shortest arc, step fields switch at w ≥ 0.5.
 * Returns a new pose; the inputs are not touched. `w ≤ 0` returns `base` itself.
 */
export function overrideBones(root: SkfRoot, base: Pose, layer: Pose, ids: readonly number[], w: number): Pose {
  const t = Number.isFinite(w) ? clamp(w, 0, 1) : 0
  if (t <= 0 || ids.length === 0) return base
  const step = t >= 0.5
  const bones = base.bones.slice()
  const visuals = base.visuals.slice()
  for (const i of ids) {
    const a = base.bones[i]
    const b = layer.bones[i]
    if (!a || !b) continue
    bones[i] = {
      pos: lerp2(a.pos, b.pos, t),
      rot: a.rot + shortestDelta(a.rot, b.rot) * t,
      scale: lerp2(a.scale, b.scale, t),
      hidden: step ? b.hidden : a.hidden,
    }
    const vid = root.bones[i]?.visuals_id ?? -1
    const av = vid >= 0 ? base.visuals[vid] : undefined
    const bv = vid >= 0 ? layer.visuals[vid] : undefined
    if (av && bv) {
      visuals[vid] = {
        tex: step ? bv.tex : av.tex,
        zindex: step ? bv.zindex : av.zindex,
        tint: {
          r: lerp(av.tint.r, bv.tint.r, t),
          g: lerp(av.tint.g, bv.tint.g, t),
          b: lerp(av.tint.b, bv.tint.b, t),
          a: lerp(av.tint.a, bv.tint.a, t),
        },
      }
    }
  }
  return { bones, visuals, ik: base.ik }
}
